import * as z from "zod/v4";
import { UlidSchema } from "./ulid.ts";
import { IsoDateTimeSchema } from "./iso.ts";
import { ResultSchemaOf } from "./result.ts";
import { nowIsoString } from "../util/date.ts";
import { MAX_FILE_SIZE_BYTES, MimeTypeSchema } from "./mime-type.ts";
import { Sha256Schema } from "./sha256.ts";

// Attachment metadata (the database record)
export const LibraryAttachmentMetaSchema = z.object({
  id: UlidSchema,
  filename: z.string().min(1).describe("Original filename"),
  mime_type: MimeTypeSchema,
  size_bytes: z.number().int().positive().max(MAX_FILE_SIZE_BYTES),
  created_at: IsoDateTimeSchema.default(nowIsoString),
});

export type LibraryAttachmentMeta = z.infer<typeof LibraryAttachmentMetaSchema>;

// Library attachment: separates file identity (sha256) from metadata
// sha256 = content hash of file bytes - used for storage lookups
// meta = database record metadata - used for display and internal references
export const LibraryAttachmentSchema = z.object({
  sha256: Sha256Schema.describe("Content hash (sha256 of file bytes)"),
  meta: LibraryAttachmentMetaSchema,
});

export type LibraryAttachment = z.infer<typeof LibraryAttachmentSchema>;

export const LibraryAttachmentArraySchema = z.array(LibraryAttachmentSchema);
export type LibraryAttachmentArray = z.infer<
  typeof LibraryAttachmentArraySchema
>;

// Library attachment with signed URL for download
export const LibraryAttachmentWithUrlSchema = z.object({
  sha256: Sha256Schema.describe("Content hash (sha256 of file bytes)"),
  meta: LibraryAttachmentMetaSchema,
  signed_url: z.url().describe("Signed URL for downloading the attachment"),
});

export type LibraryAttachmentWithUrl = z.infer<
  typeof LibraryAttachmentWithUrlSchema
>;

// Result types
export const LibraryAttachmentResultSchema = ResultSchemaOf(
  LibraryAttachmentSchema,
  z.string(),
);
export type LibraryAttachmentResult = z.infer<
  typeof LibraryAttachmentResultSchema
>;

export const LibraryAttachmentArrayResultSchema = ResultSchemaOf(
  LibraryAttachmentArraySchema,
  z.string(),
);
export type LibraryAttachmentArrayResult = z.infer<
  typeof LibraryAttachmentArrayResultSchema
>;

export const LibraryAttachmentWithUrlResultSchema = ResultSchemaOf(
  LibraryAttachmentWithUrlSchema,
  z.string(),
);
export type LibraryAttachmentWithUrlResult = z.infer<
  typeof LibraryAttachmentWithUrlResultSchema
>;
